import React from "react";

import { AWSRegion } from "src/constants/AwsRegions";
import { Tooltip } from "src/design-system/Tooltip";

/**
 * Configuration for model rerouting by workspace region.
 * Maps AWS region identifier to the target region where requests are processed.
 */
const REGION_ROUTING_CONFIG: Partial<
  Record<AWSRegion, { targetRegion: string }>
> = {
  "eu-west-2": { targetRegion: "EU" }, // UK -> EU region
  "ca-central-1": { targetRegion: "US/CA" }, // Canada -> US region
  "sa-east-1": { targetRegion: "Global" }, // Brazil -> Global region
  "ap-south-1": { targetRegion: "Global" }, // Mumbai -> Global region
  "ap-southeast-1": { targetRegion: "Global" }, // Singapore -> Global region
};

/**
 * Models that should show the rerouted badge when in a rerouted region.
 */
const REROUTED_MODEL_IDS = [
  "claude-4-6-opus",
  "claude-4-5-sonnet",
  "claude-4-5-haiku",
];

/**
 * Returns the target region if this model is rerouted for the given workspace region.
 * Returns undefined if the model is not rerouted.
 */
export const getRoutedRegion = (
  modelId: string,
  workspaceRegionAWS?: AWSRegion,
): string | undefined => {
  if (!workspaceRegionAWS) return undefined;
  const config = REGION_ROUTING_CONFIG[workspaceRegionAWS];
  if (!config || !REROUTED_MODEL_IDS.includes(modelId)) return undefined;
  return config.targetRegion;
};

type RegionRoutingBadgeProps = {
  targetRegion: string;
};

/**
 * Badge indicating that a model is processed in a different region.
 */
export const RegionRoutingBadge: React.FC<RegionRoutingBadgeProps> = ({
  targetRegion,
}) => {
  return (
    <Tooltip
      body={
        <div>
          <div className="mb-1">
            This model is not available in your region. Requests will be
            processed in the{" "}
            <span className="font-semibold">{targetRegion}</span> to ensure
            availability.
          </div>
          <a
            className="text-indigo-400 underline hover:text-indigo-300"
            href="https://docs.taktile.com/decision-flow-fundamentals/node-types/ai-node#model-selection"
            rel="noopener noreferrer"
            target="_blank"
          >
            Learn more
          </a>
        </div>
      }
      placement="right"
      asChild
    >
      <div className="flex h-5 shrink-0 items-center rounded bg-yellow-100 px-2 py-1">
        <span className="font-inter-normal-12px text-yellow-700">
          Processed in {targetRegion}
        </span>
      </div>
    </Tooltip>
  );
};
